import React, { useState, useEffect } from 'react';
import './ReadingPlanPanel.css';
import {
  PLANS, parsePassage, getDayIndex, getTodayString,
  loadPlanState, savePlanState, clearPlanState, getStreak,
} from '../utils/readingPlanUtils';
import { localizeBookName } from '../utils/bibleLocale';

const UI = {
  en: {
    title:        'Reading Plan',
    pickerSub:    'Read the entire Bible in one year. Pick a plan to start today.',
    startToday:   'Start today →',
    todaysReading: "Today's Reading",
    markAll:      'Mark All as Read',
    allDone:      'Today Complete!',
    switchPlan:   'Switch Plan',
    cancel:       'Cancel',
    currentPlan:  'Current plan',
    switchTo:     'Switch to this →',
    dayOf:        (n) => `Day ${n} / 365`,
    daysComplete: (n) => `${n} of 365 days complete`,
    streak:       (n) => `🔥 ${n} day${n !== 1 ? 's' : ''}`,
    confirmText:  (name, count) => `Switch plan? Your progress on ${name} (${count} days) will be lost.`,
    dateLocale:   'en-US',
  },
  zh: {
    title:        '讀經計劃',
    pickerSub:    '一年讀完整本聖經。選擇一個計劃，從今天開始。',
    startToday:   '立即開始 →',
    todaysReading: '今日讀經',
    markAll:      '標記全部已讀',
    allDone:      '今日完成！',
    switchPlan:   '更換計劃',
    cancel:       '取消',
    currentPlan:  '目前計劃',
    switchTo:     '切換至此 →',
    dayOf:        (n) => `第 ${n} 天／365`,
    daysComplete: (n) => `已完成 ${n}／365 天`,
    streak:       (n) => `🔥 連續 ${n} 天`,
    confirmText:  (name, count) => `確定更換計劃？「${name}」的進度（${count} 天）將會遺失。`,
    dateLocale:   'zh-TW',
  },
  fr: {
    title:        'Plan de lecture',
    pickerSub:    'Lisez toute la Bible en un an. Choisissez un plan pour commencer aujourd\'hui.',
    startToday:   'Commencer →',
    todaysReading: "Lecture du jour",
    markAll:      'Tout marquer comme lu',
    allDone:      'Journée terminée !',
    switchPlan:   'Changer de plan',
    cancel:       'Annuler',
    currentPlan:  'Plan actuel',
    switchTo:     'Choisir ce plan →',
    dayOf:        (n) => `Jour ${n} / 365`,
    daysComplete: (n) => `${n} jours sur 365 complétés`,
    streak:       (n) => `🔥 ${n} jour${n !== 1 ? 's' : ''} de suite`,
    confirmText:  (name, count) => `Changer de plan ? Votre progression sur ${name} (${count} jours) sera perdue.`,
    dateLocale:   'fr-FR',
  },
};

// Localized plan metadata
const PLAN_META = {
  mcheyne: {
    en: { name: "M'Cheyne One-Year",        desc: '4 readings per day',   detail: 'Two OT + two NT passages daily' },
    zh: { name: '麥基尼一年讀經計劃',           desc: '每日 4 段經文',          detail: '每日兩段舊約＋兩段新約' },
    fr: { name: "Plan M'Cheyne en un an",   desc: '4 lectures par jour',  detail: 'Deux AT + deux NT par jour' },
  },
  esvthroughthebible: {
    en: { name: 'Through the Bible',        desc: '2 readings per day',   detail: 'OT + NT read in parallel' },
    zh: { name: '通讀聖經',                   desc: '每日 2 段經文',          detail: '舊約＋新約平行閱讀' },
    fr: { name: 'À travers la Bible',       desc: '2 lectures par jour',  detail: 'AT + NT en parallèle' },
  },
  oneyearchronological: {
    en: { name: 'One Year Chronological',   desc: '1 reading per day',    detail: 'Bible in historical order' },
    zh: { name: '按時序一年讀經',              desc: '每日 1 段經文',           detail: '按聖經歷史順序閱讀' },
    fr: { name: 'Chronologique en un an',   desc: '1 lecture par jour',   detail: 'Bible dans l\'ordre historique' },
  },
};

function getPlanMeta(planId, language) {
  const lang = language === 'zh-s' ? 'zh' : (language || 'en');
  return PLAN_META[planId]?.[lang] ?? PLAN_META[planId]?.en ?? {};
}

/** Replace the English book name in a passage string with the localized name. */
function localizePassage(passageStr, language) {
  if (!language || language === 'en') return passageStr;
  const parsed = parsePassage(passageStr);
  if (!parsed) return passageStr;
  // Extract the chapter/verse portion after the book name
  const m = passageStr.trim().match(/^.*[a-zA-Z]\s+(.+)$/);
  if (!m) return passageStr;
  const localized = localizeBookName(parsed.book, language);
  return `${localized} ${m[1]}`;
}

export default function ReadingPlanPanel({ onClose, onNavigate, isLightMode, language = 'en' }) {
  const [planState, setPlanState] = useState(() => loadPlanState());
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  const lang = language === 'zh-s' ? 'zh' : language;
  const t = UI[lang] ?? UI.en;

  useEffect(() => {
    if (planState) savePlanState(planState);
  }, [planState]);

  const activePlan = planState ? PLANS.find(p => p.id === planState.planId) : null;
  const dayIndex = planState ? getDayIndex(planState.startDate) : 0;
  const todaysPassages = activePlan ? (activePlan.data.data2[dayIndex] || []) : [];

  // A day is "done" if it's in completedDays (either auto or via mark-all)
  const isTodayDone = planState?.completedDays?.includes(dayIndex) ?? false;

  // Per-passage completion for today: if the whole day is done, all passages are done
  const rawChecked = planState?.completedPassages?.[dayIndex] ?? [];
  const checkedPassages = new Set(isTodayDone
    ? todaysPassages.map((_, i) => i)
    : rawChecked);

  const allChecked = todaysPassages.length > 0 && checkedPassages.size === todaysPassages.length;
  const streak = planState ? getStreak(planState.completedDays, planState.startDate) : 0;
  const completedCount = planState?.completedDays?.length ?? 0;
  const progressPct = (completedCount / 365) * 100;

  const todayFormatted = new Date().toLocaleDateString(t.dateLocale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  // ── Actions ──

  const startPlan = (planId) => {
    setPlanState({ planId, startDate: getTodayString(), completedDays: [], completedPassages: {} });
    setConfirmSwitch(false);
  };

  const togglePassage = (i) => {
    if (isTodayDone) return; // day already marked complete, nothing to untoggle
    setPlanState(prev => {
      const current = new Set(prev.completedPassages?.[dayIndex] ?? []);
      if (current.has(i)) {
        current.delete(i);
      } else {
        current.add(i);
      }
      const nextChecked = [...current];
      const allDone = nextChecked.length === todaysPassages.length;
      return {
        ...prev,
        completedPassages: { ...prev.completedPassages, [dayIndex]: nextChecked },
        completedDays: allDone && !prev.completedDays.includes(dayIndex)
          ? [...prev.completedDays, dayIndex]
          : prev.completedDays,
      };
    });
  };

  const markAllToday = () => {
    if (isTodayDone) return;
    setPlanState(prev => ({
      ...prev,
      completedPassages: {
        ...prev.completedPassages,
        [dayIndex]: todaysPassages.map((_, i) => i),
      },
      completedDays: prev.completedDays.includes(dayIndex)
        ? prev.completedDays
        : [...prev.completedDays, dayIndex],
    }));
  };

  const handlePassageNavigate = (passageStr, passageIndex, e) => {
    e.stopPropagation();
    const parsed = parsePassage(passageStr);
    if (!parsed) return;
    // Auto-mark this passage as read when the user navigates to it
    if (!checkedPassages.has(passageIndex)) {
      setPlanState(prev => {
        const current = new Set(prev.completedPassages?.[dayIndex] ?? []);
        current.add(passageIndex);
        const nextChecked = [...current];
        const allDone = nextChecked.length === todaysPassages.length;
        return {
          ...prev,
          completedPassages: { ...prev.completedPassages, [dayIndex]: nextChecked },
          completedDays: allDone && !prev.completedDays.includes(dayIndex)
            ? [...prev.completedDays, dayIndex]
            : prev.completedDays,
        };
      });
    }
    onNavigate(parsed.bookIndex, parsed.chapterIndex);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`ReadingPlan-overlay${isLightMode ? ' light-mode' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="ReadingPlan-modal">

        {/* Header */}
        <div className="ReadingPlan-header">
          <h2 className="ReadingPlan-title">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {t.title}
          </h2>
          <button className="ReadingPlan-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!activePlan ? (
          /* ── Plan picker ── */
          <div className="ReadingPlan-picker">
            <p className="ReadingPlan-picker-sub">{t.pickerSub}</p>
            <div className="ReadingPlan-cards">
              {PLANS.map(plan => {
                const meta = getPlanMeta(plan.id, language);
                return (
                  <button key={plan.id} className="ReadingPlan-card" onClick={() => startPlan(plan.id)}>
                    <div className="ReadingPlan-card-name">{meta.name}</div>
                    <div className="ReadingPlan-card-desc">{meta.desc}</div>
                    <div className="ReadingPlan-card-detail">{meta.detail}</div>
                    <div className="ReadingPlan-card-cta">{t.startToday}</div>
                  </button>
                );
              })}
            </div>
          </div>

        ) : confirmSwitch ? (
          /* ── Switch confirmation ── */
          <div className="ReadingPlan-confirm">
            <p className="ReadingPlan-confirm-text">
              {t.confirmText(getPlanMeta(activePlan.id, language).name, completedCount)}
            </p>
            <div className="ReadingPlan-cards">
              {PLANS.map(plan => {
                const meta = getPlanMeta(plan.id, language);
                return (
                  <button
                    key={plan.id}
                    className={`ReadingPlan-card${plan.id === activePlan.id ? ' is-active' : ''}`}
                    onClick={() => { clearPlanState(); startPlan(plan.id); }}
                  >
                    <div className="ReadingPlan-card-name">{meta.name}</div>
                    <div className="ReadingPlan-card-desc">{meta.desc}</div>
                    <div className="ReadingPlan-card-detail">{meta.detail}</div>
                    {plan.id === activePlan.id
                      ? <div className="ReadingPlan-card-cta ReadingPlan-card-cta--current">{t.currentPlan}</div>
                      : <div className="ReadingPlan-card-cta">{t.switchTo}</div>}
                  </button>
                );
              })}
            </div>
            <button className="ReadingPlan-switch" onClick={() => setConfirmSwitch(false)}>{t.cancel}</button>
          </div>

        ) : (
          /* ── Active plan view ── */
          <div className="ReadingPlan-active">

            {/* Meta row */}
            <div className="ReadingPlan-meta">
              <div className="ReadingPlan-meta-left">
                <div className="ReadingPlan-plan-name">{getPlanMeta(activePlan.id, language).name}</div>
                <div className="ReadingPlan-date">{todayFormatted}</div>
              </div>
              <div className="ReadingPlan-meta-right">
                {streak > 0 && (
                  <div className="ReadingPlan-streak">{t.streak(streak)}</div>
                )}
                <div className="ReadingPlan-day-badge">{t.dayOf(dayIndex + 1)}</div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="ReadingPlan-progress-wrap">
              <div className="ReadingPlan-progress-bar">
                <div className="ReadingPlan-progress-fill" style={{ width: `${Math.max(progressPct, 0.5)}%` }} />
              </div>
              <span className="ReadingPlan-progress-label">{t.daysComplete(completedCount)}</span>
            </div>

            {/* Today's readings */}
            <div className="ReadingPlan-section-label">{t.todaysReading}</div>
            <div className="ReadingPlan-passages">
              {todaysPassages.map((passage, i) => {
                const parsed = parsePassage(passage);
                const done = checkedPassages.has(i);
                return (
                  <div key={i} className={`ReadingPlan-passage-row${done ? ' is-done' : ''}`}>
                    {/* Navigate area */}
                    <button
                      className="ReadingPlan-passage-nav"
                      onClick={(e) => handlePassageNavigate(passage, i, e)}
                      disabled={!parsed}
                      title={parsed ? `Open ${parsed.book} ${parsed.chapter}` : 'Cannot navigate'}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                      </svg>
                      <span className="ReadingPlan-passage-text">{localizePassage(passage, language)}</span>
                      <svg className="ReadingPlan-chevron" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>

                    {/* Per-passage checkmark */}
                    <button
                      className={`ReadingPlan-check-btn${done ? ' is-checked' : ''}`}
                      onClick={() => togglePassage(i)}
                      aria-label={done ? 'Mark as unread' : 'Mark as read'}
                      title={done ? 'Mark as unread' : 'Mark as read'}
                    >
                      {done ? (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="9"/>
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Mark all / complete status */}
            <button
              className={`ReadingPlan-mark-btn${allChecked ? ' is-done' : ''}`}
              onClick={markAllToday}
              disabled={allChecked}
            >
              {allChecked ? (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {t.allDone}
                </>
              ) : t.markAll}
            </button>

            {/* Switch plan */}
            <button className="ReadingPlan-switch" onClick={() => setConfirmSwitch(true)}>
              {t.switchPlan}
            </button>

          </div>
        )}
      </div>
    </div>
  );
}
